import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { addCalendarDays, getBusinessDateString, parseAsLocalDate } from '@/utils/dateHelpers';
import { Loader2, DollarSign, Wallet, History, AlertCircle, Clock, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { useAuth } from '@/contexts/useAuth';
import { notificationService } from '@/services/notificationService';
import { getReconDocumentsByIds, type ReconDocument } from '@/services/bankerReconService';
import { ReconDocumentPopover } from './ReconDocumentPopover';
import { buildCompSetInputFromDirtyFields, compSet } from '@/services/compensationService';
import { featureFlagService } from '@/services/featureFlagService';
import { ObCompRequestsPanel } from './ObCompRequestsPanel';

const log = createComponentLogger('CompensationDrawer', 'compensation');

// Validation schema
export const compensationFormSchema = z.object({
  maximum_compensation: z.number().min(10000).max(999999).nullable(),
  banked_compensation: z.number().min(0.75).max(3.00).nullable(),
  broker_commission_split: z.number().min(0).max(85).nullable(),
  banked_override_bps: z.number().min(0).max(50).nullable(),
  broker_mentor_commission_split: z.number().min(0).max(50).nullable(),
  override_recipient_id: z.string().nullable(),
  mentor_id: z.string().nullable()
});

type CompensationFormValues = z.infer<typeof compensationFormSchema>;

interface UserData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  maximum_compensation: number | null;
  banked_compensation: number | null;
  broker_commission_split: number | null;
  banked_override_bps: number | null;
  broker_mentor_commission_split: number | null;
  override_recipient_id: string | null;
  mentor_id: string | null;
  mentorship_active: boolean;
  brokered_deals_completed: number;
  // Pending compensation request fields
  pending_banked_compensation: number | null;
  pending_maximum_compensation: number | null;
  compensation_request_justification: string | null;
  compensation_request_effective_date: string | null;
  compensation_request_submitted_at: string | null;
  compensation_request_status: 'pending' | 'approved' | 'rejected' | null;
  compensation_request_reviewed_by: string | null;
  compensation_request_reviewed_at: string | null;
  compensation_request_reviewer_notes: string | null;
  optimal_blue_current_schedule_scope?: string | null;
  [key: string]: any;
}

interface CompensationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserData | null;
  potentialMentors: UserData[];
  onSave?: () => void;
}

const BANKED_COMPENSATION_OPTIONS = [
  { value: '0.75', label: '0.75%' },
  { value: '1.00', label: '1.00%' },
  { value: '1.25', label: '1.25%' },
  { value: '1.50', label: '1.50%' },
  { value: '1.75', label: '1.75%' },
  { value: '2.00', label: '2.00%' },
  { value: '2.25', label: '2.25%' },
  { value: '2.50', label: '2.50%' },
  { value: '2.75', label: '2.75%' },
  { value: '3.00', label: '3.00%' }
];

// Types for payouts and history data
interface PayoutRecord {
  id: string;
  loanNumber: string;
  type: 'Banked' | 'Brokered' | 'Override' | 'Mentor';
  source: string;
  loanAmount: number;
  grossPayout: number;
  eAndO: number;
  splits: string | null;
  netPayout: number;
  fundDate: string;
  status: 'pending' | 'paid';
  hitCap: boolean;
  settlementStatementPath: string | null;
  finalCheckPath: string | null;
  bankerReconId: string | null;
  reconDocuments: ReconDocument[];
}

interface HistoryRecord {
  id: string;
  effectiveDate: string;
  changedBy: string | null;
  maximumCompensation: number | null;
  bankedCompensation: number | null;
  brokerCommissionSplit: number | null;
  bankedOverrideBps: number | null;
  brokerMentorCommissionSplit: number | null;
  overrideRecipientName: string | null;
  mentorName: string | null;
  mentorshipActive: boolean | null;
}

export const CompensationDrawer: React.FC<CompensationDrawerProps> = ({
  open,
  onOpenChange,
  user,
  potentialMentors,
  onSave
}) => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('compensation');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingRequest, setIsProcessingRequest] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [obCompFeatureEnabled, setObCompFeatureEnabled] = useState(false);
  const minimumBrokerSplitEffectiveDate = addCalendarDays(getBusinessDateString(), 1);
  const [brokerSplitEffectiveDate, setBrokerSplitEffectiveDate] = useState(
    minimumBrokerSplitEffectiveDate
  );

  useEffect(() => {
    void (async () => {
      const enabled = await featureFlagService.isObCompensationFeatureEnabled(currentUser?.id);
      setObCompFeatureEnabled(enabled);
    })();
  }, [currentUser?.id]);
  const [rejectionNotes, setRejectionNotes] = useState('');

  // Payouts state
  const [payouts, setPayouts] = useState<PayoutRecord[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);

  // History state
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Check if user has a pending compensation request
  const hasPendingRequest = user && (
    user.pending_banked_compensation !== null ||
    user.pending_maximum_compensation !== null
  ) && user.compensation_request_status !== 'approved' && user.compensation_request_status !== 'rejected';

  const form = useForm<CompensationFormValues>({
    resolver: zodResolver(compensationFormSchema),
    defaultValues: {
      maximum_compensation: null,
      banked_compensation: null,
      broker_commission_split: null,
      banked_override_bps: null,
      broker_mentor_commission_split: null,
      override_recipient_id: null,
      mentor_id: null
    }
  });

  const { formState: { dirtyFields, errors, isDirty } } = form;

  // Load user data into form when user changes
  useEffect(() => {
    if (user) {
      form.reset({
        maximum_compensation: user.maximum_compensation,
        banked_compensation: user.banked_compensation,
        broker_commission_split: user.broker_commission_split,
        banked_override_bps: user.banked_override_bps,
        broker_mentor_commission_split: user.broker_mentor_commission_split,
        override_recipient_id: user.override_recipient_id,
        mentor_id: user.mentor_id
      });
      setBrokerSplitEffectiveDate(minimumBrokerSplitEffectiveDate);
    }
  }, [user, form, minimumBrokerSplitEffectiveDate]);

  // Fetch payouts for this user
  useEffect(() => {
    if (!user || activeTab !== 'payouts') {
return;
}

    const fetchPayouts = async () => {
      setPayoutsLoading(true);
      try {
        const { data, error } = await supabase
          .from('payouts')
          .select(`
            id,
            loan_number,
            loan_amount,
            payout_type,
            gross_amount,
            e_and_o_deduction,
            net_amount,
            hit_compensation_cap,
            splits_description,
            fund_date,
            status,
            settlement_statement_path,
            final_check_path,
            banker_recon_id,
            source_officer:source_officer_id (first_name, last_name)
          `)
          .eq('recipient_id', user.id)
          .order('fund_date', { ascending: false });

        if (error) {
throw error;
}

        const transformedPayouts: PayoutRecord[] = (data || []).map((record: {
          id: string;
          loan_number: string;
          loan_amount: number;
          payout_type: string;
          gross_amount: number;
          e_and_o_deduction: number;
          net_amount: number;
          hit_compensation_cap: boolean;
          splits_description: string | null;
          fund_date: string;
          status: string;
          settlement_statement_path: string | null;
          final_check_path: string | null;
          banker_recon_id: string | null;
          source_officer: { first_name: string; last_name: string } | null;
        }) => {
          const typeMap: Record<string, 'Banked' | 'Brokered' | 'Override' | 'Mentor'> = {
            banked: 'Banked',
            brokered: 'Brokered',
            override: 'Override',
            mentor: 'Mentor'
          };

          return {
            id: record.id,
            loanNumber: record.loan_number,
            type: typeMap[record.payout_type] || 'Banked',
            source: record.source_officer
              ? `${record.source_officer.first_name} ${record.source_officer.last_name}`
              : 'Own Deal',
            loanAmount: Number(record.loan_amount),
            grossPayout: Number(record.gross_amount),
            eAndO: Number(record.e_and_o_deduction),
            splits: record.splits_description,
            netPayout: Number(record.net_amount),
            fundDate: record.fund_date,
            status: record.status as 'pending' | 'paid',
            hitCap: record.hit_compensation_cap,
            settlementStatementPath: record.settlement_statement_path,
            finalCheckPath: record.final_check_path,
            bankerReconId: record.banker_recon_id || null,
            reconDocuments: []
          };
        });

        // Enrich banked/override payouts with all linked recon documents
        const reconIds = [...new Set(transformedPayouts.map(p => p.bankerReconId).filter((id): id is string => !!id))];
        if (reconIds.length > 0) {
          try {
            const docMap = await getReconDocumentsByIds(reconIds);
            for (const payout of transformedPayouts) {
              if (payout.bankerReconId && docMap[payout.bankerReconId]) {
                payout.reconDocuments = docMap[payout.bankerReconId];
              }
            }
          } catch (err) {
            log.warn('Recon document enrichment failed, falling back to scalar paths', { error: (err as Error).message });
          }
        }

        setPayouts(transformedPayouts);
        log.info('User payouts loaded', { userId: user.id, count: transformedPayouts.length });
      } catch (error) {
        log.error('Failed to fetch user payouts', { userId: user.id }, error as Error);
        toast.error('Failed to load payout history');
      } finally {
        setPayoutsLoading(false);
      }
    };

    fetchPayouts();
  }, [user?.id, activeTab]);

  // Fetch compensation history for this user
  useEffect(() => {
    if (!user || activeTab !== 'history') {
return;
}

    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const { data, error } = await supabase
          .from('compensation_history')
          .select(`
            id,
            effective_date,
            maximum_compensation,
            banked_compensation,
            broker_commission_split,
            banked_override_bps,
            broker_mentor_commission_split,
            mentorship_active,
            changed_by,
            changer:changed_by (first_name, last_name),
            override_recipient:override_recipient_id (first_name, last_name),
            mentor:mentor_id (first_name, last_name)
          `)
          .eq('user_id', user.id)
          .order('effective_date', { ascending: false });

        if (error) {
throw error;
}

        const transformedHistory: HistoryRecord[] = (data || []).map((record: {
          id: string;
          effective_date: string;
          maximum_compensation: number | null;
          banked_compensation: number | null;
          broker_commission_split: number | null;
          banked_override_bps: number | null;
          broker_mentor_commission_split: number | null;
          mentorship_active: boolean | null;
          changed_by: string | null;
          changer: { first_name: string; last_name: string } | null;
          override_recipient: { first_name: string; last_name: string } | null;
          mentor: { first_name: string; last_name: string } | null;
        }) => ({
          id: record.id,
          effectiveDate: record.effective_date,
          changedBy: record.changer
            ? `${record.changer.first_name} ${record.changer.last_name}`
            : null,
          maximumCompensation: record.maximum_compensation,
          bankedCompensation: record.banked_compensation ? Number(record.banked_compensation) : null,
          brokerCommissionSplit: record.broker_commission_split ? Number(record.broker_commission_split) : null,
          bankedOverrideBps: record.banked_override_bps,
          brokerMentorCommissionSplit: record.broker_mentor_commission_split ? Number(record.broker_mentor_commission_split) : null,
          overrideRecipientName: record.override_recipient
            ? `${record.override_recipient.first_name} ${record.override_recipient.last_name}`
            : null,
          mentorName: record.mentor
            ? `${record.mentor.first_name} ${record.mentor.last_name}`
            : null,
          mentorshipActive: record.mentorship_active
        }));

        setHistory(transformedHistory);
        log.info('User compensation history loaded', { userId: user.id, count: transformedHistory.length });
      } catch (error) {
        log.error('Failed to fetch compensation history', { userId: user.id }, error as Error);
        toast.error('Failed to load compensation history');
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchHistory();
  }, [user?.id, activeTab]);

  const handleClose = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close?'
      );
      if (!confirmed) {
return;
}
    }
    if (user) {
      form.reset({
        maximum_compensation: user.maximum_compensation,
        banked_compensation: user.banked_compensation,
        broker_commission_split: user.broker_commission_split,
        banked_override_bps: user.banked_override_bps,
        broker_mentor_commission_split: user.broker_mentor_commission_split,
        override_recipient_id: user.override_recipient_id,
        mentor_id: user.mentor_id
      });
    }
    setBrokerSplitEffectiveDate(minimumBrokerSplitEffectiveDate);
    onOpenChange(false);
  };

  const onSubmit = async (data: CompensationFormValues) => {
    if (!user) {
return;
}

    if (
      dirtyFields.broker_commission_split &&
      (!brokerSplitEffectiveDate || brokerSplitEffectiveDate < minimumBrokerSplitEffectiveDate)
    ) {
      toast.error('Choose a future broker split effective date', {
        description: 'Broker split changes apply only to fundings on or after the selected date.'
      });
      return;
    }

    const changes = buildCompSetInputFromDirtyFields(
      user.id,
      data,
      dirtyFields,
      brokerSplitEffectiveDate
    );

    if (Object.keys(changes).length === 1) {
      return;
    }

    try {
      setIsSubmitting(true);
      log.info('Updating user compensation', {
        userId: user.id,
        metadata: { fields: Object.keys(changes).filter(key => key !== 'userId') }
      });

      // Routes through the comp-set edge function → rpc_comp_set. The trigger on `users`
      // (enforce_comp_write_authorization) blocks direct .update of guarded columns.
      // comp-set internally skips the OB-scope check when ob_compensation_feature is OFF
      // (preserves legacy admin behavior during the rollout window).
      await compSet(changes);

      toast.success(dirtyFields.broker_commission_split
        ? 'Broker split scheduled'
        : 'Compensation updated successfully', dirtyFields.broker_commission_split ? {
        description: `Applies only to brokered loans funded on or after ${brokerSplitEffectiveDate}. Existing payout snapshots stay unchanged.`
      } : undefined);
      form.reset(data);
      onSave?.();
      onOpenChange(false);

      log.info('Compensation updated successfully', { userId: user.id });
    } catch (error) {
      log.error('Failed to update compensation', { userId: user?.id }, error as Error);
      toast.error('Failed to update compensation', {
        description: (error as Error).message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle approving a compensation request
  const handleApproveRequest = async () => {
    if (!user || !currentUser) {
return;
}

    try {
      setIsProcessingRequest(true);
      log.info('Approving compensation request', { userId: user.id });

      // Apply the pending values via comp-set (rpc_comp_set) — trigger guards direct .update
      // of banked_compensation / maximum_compensation. Pending_* + request_* columns are NOT
      // guarded, so we still clear them via direct .update below.
      const newBanked = user.pending_banked_compensation ?? null;
      const newMax = user.pending_maximum_compensation ?? null;
      if (newBanked !== null || newMax !== null) {
        await compSet({
          userId: user.id,
          banked_compensation: newBanked,
          maximum_compensation: newMax
        });
      }

      // Clear the pending request columns + mark the request approved (legacy columns
      // until Migration B drops them in favor of compensation_change_requests).
      const { error } = await supabase
        .from('users')
        .update({
          compensation_request_status: 'approved',
          compensation_request_reviewed_by: currentUser.id,
          compensation_request_reviewed_at: new Date().toISOString(),
          pending_banked_compensation: null,
          pending_maximum_compensation: null,
          compensation_request_justification: null,
          compensation_request_effective_date: null,
          compensation_request_submitted_at: null
        })
        .eq('id', user.id);

      if (error) {
throw error;
}

      toast.success('Compensation request approved', {
        description: `${user.first_name}'s compensation has been updated.`
      });

      log.info('Compensation request approved', {
        userId: user.id,
        newBanked: user.pending_banked_compensation,
        newMax: user.pending_maximum_compensation
      });

      // Send notification email to the loan officer
      try {
        await notificationService.sendCompensationApprovedEmail({
          userId: user.id,
          firstName: user.first_name,
          previousBankedComp: user.banked_compensation?.toString() || null,
          newBankedComp: user.pending_banked_compensation?.toString() || null,
          previousMaxComp: user.maximum_compensation ? `$${user.maximum_compensation.toLocaleString()}` : null,
          newMaxComp: user.pending_maximum_compensation ? `$${user.pending_maximum_compensation.toLocaleString()}` : null,
          approvedBy: `${currentUser.first_name} ${currentUser.last_name}`
        });
      } catch (notifyError) {
        log.warn('Failed to send approval notification email', { userId: user.id }, notifyError as Error);
        // Don't fail the approval if notification fails
      }

      onSave?.();
      onOpenChange(false);
    } catch (error) {
      log.error('Failed to approve compensation request', { userId: user?.id }, error as Error);
      toast.error('Failed to approve request', {
        description: (error as Error).message
      });
    } finally {
      setIsProcessingRequest(false);
    }
  };

  // Handle rejecting a compensation request
  const handleRejectRequest = async () => {
    if (!user || !currentUser) {
return;
}

    try {
      setIsProcessingRequest(true);
      log.info('Rejecting compensation request', { userId: user.id });

      const { error } = await supabase
        .from('users')
        .update({
          compensation_request_status: 'rejected',
          compensation_request_reviewed_by: currentUser.id,
          compensation_request_reviewed_at: new Date().toISOString(),
          compensation_request_reviewer_notes: rejectionNotes || null,
          // Clear the pending fields
          pending_banked_compensation: null,
          pending_maximum_compensation: null,
          compensation_request_justification: null,
          compensation_request_effective_date: null,
          compensation_request_submitted_at: null
        })
        .eq('id', user.id);

      if (error) {
throw error;
}

      toast.success('Compensation request rejected', {
        description: `${user.first_name} will be notified of the decision.`
      });

      log.info('Compensation request rejected', { userId: user.id, notes: rejectionNotes });

      // Send notification email to the loan officer
      try {
        await notificationService.sendCompensationRejectedEmail({
          userId: user.id,
          firstName: user.first_name,
          requestedBankedComp: user.pending_banked_compensation?.toString() || null,
          requestedMaxComp: user.pending_maximum_compensation ? `$${user.pending_maximum_compensation.toLocaleString()}` : null,
          rejectedBy: `${currentUser.first_name} ${currentUser.last_name}`,
          rejectionNotes: rejectionNotes || undefined
        });
      } catch (notifyError) {
        log.warn('Failed to send rejection notification email', { userId: user.id }, notifyError as Error);
        // Don't fail the rejection if notification fails
      }

      setShowRejectDialog(false);
      setRejectionNotes('');
      onSave?.();
      onOpenChange(false);
    } catch (error) {
      log.error('Failed to reject compensation request', { userId: user?.id }, error as Error);
      toast.error('Failed to reject request', {
        description: (error as Error).message
      });
    } finally {
      setIsProcessingRequest(false);
    }
  };

  if (!user) {
return null;
}

  const isObManaged = obCompFeatureEnabled && (
    user.optimal_blue_current_schedule_scope === 'core' ||
    user.optimal_blue_current_schedule_scope === 'full_footprint'
  );

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="!w-[60vw] !max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {user.first_name} {user.last_name} - Compensation Details
          </SheetTitle>
          <SheetDescription>
            Configure compensation structure and view relationships
          </SheetDescription>
        </SheetHeader>

        {/* OB-driven compensation_change_requests panel (plan v5). When the feature flag is on,
            this is the canonical request UI for this user; the legacy pending_* alert below is
            hidden. When the flag is off, the legacy alert renders as before. */}
        {obCompFeatureEnabled && user && (
          <div className="mt-4">
            <ObCompRequestsPanel mode="admin" userId={user.id} onChange={() => onSave?.()} />
          </div>
        )}

        {/* Pending Request Banner (legacy users.pending_* flow) */}
        {!obCompFeatureEnabled && hasPendingRequest && (
          <Alert className="mt-4 border-amber-500 bg-amber-50">
            <Clock className="h-4 w-4 text-amber-600" />
            <AlertDescription className="ml-2">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-amber-800">Pending Compensation Request</span>
                  <Badge variant="outline" className="border-amber-500 text-amber-700">
                    Awaiting Review
                  </Badge>
                </div>

                <div className="text-sm text-amber-700 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Submitted:</span>
                    <span>
                      {user.compensation_request_submitted_at
                        ? new Date(user.compensation_request_submitted_at).toLocaleDateString()
                        : 'Unknown'}
                    </span>
                    <span className="text-muted-foreground mx-2">|</span>
                    <span className="text-muted-foreground">Effective:</span>
                    <span>
                      {user.compensation_request_effective_date
                        ? parseAsLocalDate(user.compensation_request_effective_date).toLocaleDateString()
                        : 'Unknown'}
                    </span>
                  </div>
                </div>

                <Separator className="bg-amber-200" />

                {/* Requested Changes */}
                <div className="grid gap-2 text-sm">
                  {user.pending_banked_compensation !== null && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-32">Banked Comp:</span>
                      <span className="font-medium">{user.banked_compensation ?? 'Not set'}%</span>
                      <ArrowRight className="h-3 w-3 text-amber-600" />
                      <span className="font-semibold text-amber-800">{user.pending_banked_compensation}%</span>
                    </div>
                  )}
                  {user.pending_maximum_compensation !== null && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground w-32">Max Comp:</span>
                      <span className="font-medium">${user.maximum_compensation?.toLocaleString() ?? 'Not set'}</span>
                      <ArrowRight className="h-3 w-3 text-amber-600" />
                      <span className="font-semibold text-amber-800">${user.pending_maximum_compensation.toLocaleString()}</span>
                    </div>
                  )}
                </div>

                {/* Justification */}
                {user.compensation_request_justification && (
                  <div className="mt-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Justification</span>
                    <p className="mt-1 text-sm text-amber-800 bg-white/50 p-2 rounded border border-amber-200">
                      "{user.compensation_request_justification}"
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleApproveRequest}
                    disabled={isProcessingRequest}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isProcessingRequest ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-1" />
                    )}
                    Approve
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => setShowRejectDialog(true)}
                    disabled={isProcessingRequest}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </div>

                {/* Reject Dialog */}
                {showRejectDialog && (
                  <div className="mt-3 p-3 bg-white rounded border border-red-200">
                    <Label htmlFor="rejection-notes" className="text-sm font-medium text-red-800">
                      Rejection Notes (optional)
                    </Label>
                    <Textarea
                      id="rejection-notes"
                      placeholder="Provide a reason for rejection..."
                      value={rejectionNotes}
                      onChange={(e) => setRejectionNotes(e.target.value)}
                      className="mt-2 text-sm"
                      rows={2}
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={handleRejectRequest}
                        disabled={isProcessingRequest}
                      >
                        {isProcessingRequest ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : null}
                        Confirm Rejection
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowRejectDialog(false);
                          setRejectionNotes('');
                        }}
                        disabled={isProcessingRequest}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {isDirty && !hasPendingRequest && (
          <Alert className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You have unsaved changes
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="compensation">
                <DollarSign className="h-4 w-4 mr-2" />
                Compensation
              </TabsTrigger>
              <TabsTrigger value="payouts">
                <Wallet className="h-4 w-4 mr-2" />
                Payouts
              </TabsTrigger>
              <TabsTrigger value="history">
                <History className="h-4 w-4 mr-2" />
                History
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Compensation Details */}
            <TabsContent value="compensation" className="space-y-6 mt-6">
              {isObManaged && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Banked compensation and the per-deal maximum are managed by Optimal Blue.
                    Use the compensation request panel above for those fields. Broker split and
                    relationship settings remain editable here.
                  </AlertDescription>
                </Alert>
              )}
              <div>
                <h3 className="text-sm font-medium mb-4">Compensation Amounts</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Banked Compensation */}
                  <div className="space-y-2">
                    <Label htmlFor="banked_compensation">Banked Compensation (%)</Label>
                    <Select
                      value={form.watch('banked_compensation') !== null && form.watch('banked_compensation') !== undefined ? form.watch('banked_compensation').toFixed(2) : ''}
                      disabled={isObManaged}
                      onValueChange={(value) => {
                        form.setValue('banked_compensation', value ? parseFloat(value) : null, { shouldDirty: true });
                      }}
                    >
                      <SelectTrigger id="banked_compensation">
                        <SelectValue placeholder="Select percentage (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {BANKED_COMPENSATION_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {isObManaged ? 'Managed by the active Optimal Blue compensation plan.' : 'Range: 0.75% - 3.00%'}
                    </p>
                  </div>

                  {/* Broker Commission Split */}
                  <div className="space-y-2">
                    <Label htmlFor="broker_commission_split">Broker Commission Split (%)</Label>
                    <Input
                      id="broker_commission_split"
                      type="text"
                      placeholder="e.g., 85%"
                      value={form.watch('broker_commission_split') != null ? `${form.watch('broker_commission_split')}%` : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        form.setValue('broker_commission_split', value ? parseFloat(value) : null, { shouldDirty: true });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">Range: 0% - 85%</p>
                    {errors.broker_commission_split && (
                      <p className="text-xs text-red-600">{errors.broker_commission_split.message}</p>
                    )}
                    {dirtyFields.broker_commission_split && (
                      <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                        <Label htmlFor="broker-commission-split-effective-date">
                          Effective for fundings on or after
                        </Label>
                        <Input
                          id="broker-commission-split-effective-date"
                          type="date"
                          min={minimumBrokerSplitEffectiveDate}
                          value={brokerSplitEffectiveDate}
                          onChange={(event) => setBrokerSplitEffectiveDate(event.target.value)}
                          aria-describedby="broker-commission-split-effective-help"
                          required
                        />
                        <p
                          id="broker-commission-split-effective-help"
                          className="text-xs text-muted-foreground"
                        >
                          Existing payout snapshots and fundings before this date keep their prior split.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Maximum Compensation */}
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="maximum_compensation">Maximum Compensation per Deal</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="maximum_compensation"
                        type="text"
                        placeholder="e.g., 50,000"
                        className="pl-7"
                        disabled={isObManaged}
                        value={form.watch('maximum_compensation')?.toLocaleString() ?? ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                          form.setValue('maximum_compensation', value ? parseInt(value) : null, { shouldDirty: true });
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {isObManaged
                        ? 'Managed by the active Optimal Blue compensation plan.'
                        : 'Maximum commission per deal ($10,000 - $999,999)'}
                    </p>
                    {errors.maximum_compensation && (
                      <p className="text-xs text-red-600">{errors.maximum_compensation.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-sm font-medium mb-4">Relationship Assignments</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Override Recipient */}
                  <div className="space-y-2">
                    <Label htmlFor="override_recipient">Override Recipient</Label>
                    <Select
                      value={form.watch('override_recipient_id') ?? 'none'}
                      onValueChange={(value) => {
                        form.setValue('override_recipient_id', value === 'none' ? null : value, { shouldDirty: true });
                      }}
                    >
                      <SelectTrigger id="override_recipient">
                        <SelectValue placeholder="Select user (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {potentialMentors
                          .filter(m => m.id !== user.id)
                          .map((mentor) => (
                            <SelectItem key={mentor.id} value={mentor.id}>
                              {mentor.first_name} {mentor.last_name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Override Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="banked_override_bps">Override Amount (bps)</Label>
                    <Input
                      id="banked_override_bps"
                      type="number"
                      min="0"
                      max="50"
                      placeholder="e.g., 15"
                      disabled={!form.watch('override_recipient_id')}
                      {...form.register('banked_override_bps', { valueAsNumber: true })}
                    />
                    <p className="text-xs text-muted-foreground">Range: 0 - 50 bps</p>
                  </div>

                  {/* Mentor */}
                  <div className="space-y-2">
                    <Label htmlFor="mentor">Mentor</Label>
                    <Select
                      value={form.watch('mentor_id') ?? 'none'}
                      onValueChange={(value) => {
                        form.setValue('mentor_id', value === 'none' ? null : value, { shouldDirty: true });
                      }}
                    >
                      <SelectTrigger id="mentor">
                        <SelectValue placeholder="Select mentor (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {potentialMentors
                          .filter(m => m.id !== user.id)
                          .map((mentor) => (
                            <SelectItem key={mentor.id} value={mentor.id}>
                              {mentor.first_name} {mentor.last_name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {user.mentorship_active && (
                      <p className="text-xs text-green-600">
                        Mentorship Active: {user.brokered_deals_completed}/5 deals completed
                      </p>
                    )}
                  </div>

                  {/* Mentor Commission Split */}
                  <div className="space-y-2">
                    <Label htmlFor="broker_mentor_commission_split">Mentor Commission Split (%)</Label>
                    <Input
                      id="broker_mentor_commission_split"
                      type="number"
                      min="0"
                      max="50"
                      step="0.01"
                      placeholder="e.g., 50"
                      disabled={!form.watch('mentor_id')}
                      {...form.register('broker_mentor_commission_split', { valueAsNumber: true })}
                    />
                    <p className="text-xs text-muted-foreground">Range: 0% - 50%</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 2: Payouts */}
            <TabsContent value="payouts" className="space-y-6 mt-6">
              {payoutsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Summary Stats - Calculated from actual payouts */}
                  {(() => {
                    const totalEarned = payouts.reduce((sum, p) => sum + p.netPayout, 0);
                    const totalEandO = payouts.reduce((sum, p) => sum + p.eAndO, 0);
                    const uniqueLoans = new Set(payouts.map(p => p.loanNumber)).size;
                    const overrideIncome = payouts.filter(p => p.type === 'Override').reduce((sum, p) => sum + p.netPayout, 0);
                    const mentorIncome = payouts.filter(p => p.type === 'Mentor').reduce((sum, p) => sum + p.netPayout, 0);
                    const directDeals = payouts.filter(p => p.type === 'Banked' || p.type === 'Brokered').length;
                    const avgPerDeal = directDeals > 0 ? totalEarned / directDeals : 0;

                    // Show different stats based on whether user has override/mentor income
                    const hasPassiveIncome = overrideIncome > 0 || mentorIncome > 0;

                    return (
                      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">Total Earned YTD</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className={`text-2xl font-bold ${totalEarned > 0 ? '' : 'text-muted-foreground'}`}>
                              {totalEarned > 0 ? `$${totalEarned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0'}
                            </div>
                          </CardContent>
                        </Card>

                        {hasPassiveIncome ? (
                          <>
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Override Income</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className={`text-2xl font-bold ${overrideIncome > 0 ? '' : 'text-muted-foreground'}`}>
                                  {overrideIncome > 0 ? `$${overrideIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0'}
                                </div>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Mentor Income</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className={`text-2xl font-bold ${mentorIncome > 0 ? '' : 'text-muted-foreground'}`}>
                                  {mentorIncome > 0 ? `$${mentorIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0'}
                                </div>
                              </CardContent>
                            </Card>
                          </>
                        ) : (
                          <>
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Avg Per Deal</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className={`text-2xl font-bold ${avgPerDeal > 0 ? '' : 'text-muted-foreground'}`}>
                                  {avgPerDeal > 0 ? `$${avgPerDeal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '--'}
                                </div>
                              </CardContent>
                            </Card>
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium text-muted-foreground">Deals Closed YTD</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className={`text-2xl font-bold ${uniqueLoans > 0 ? '' : 'text-muted-foreground'}`}>
                                  {uniqueLoans}
                                </div>
                              </CardContent>
                            </Card>
                          </>
                        )}

                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">E&O Deductions YTD</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className={`text-2xl font-bold ${totalEandO > 0 ? '' : 'text-muted-foreground'}`}>
                              {totalEandO > 0 ? `$${totalEandO}` : '$0'}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })()}

                  {/* Payout History Table */}
                  <div>
                    <h3 className="text-sm font-medium mb-4">Payout History</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Loan #</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Loan Amount</TableHead>
                          <TableHead>Gross</TableHead>
                          <TableHead>E&O</TableHead>
                          <TableHead>Details</TableHead>
                          <TableHead className="text-right">Net Payout</TableHead>
                          <TableHead className="text-center w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payouts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                              No payout history available
                            </TableCell>
                          </TableRow>
                        ) : (
                          payouts.map((payout) => (
                            <TableRow key={payout.id}>
                              <TableCell className="font-medium">{payout.loanNumber}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className={
                                  payout.type === 'Banked' ? 'bg-green-100 text-green-800 border-green-300' :
                                  payout.type === 'Brokered' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                  payout.type === 'Override' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                                  payout.type === 'Mentor' ? 'bg-purple-100 text-purple-800 border-purple-300' : ''
                                }>
                                  {payout.type}
                                </Badge>
                              </TableCell>
                              <TableCell>{payout.source}</TableCell>
                              <TableCell>${payout.loanAmount.toLocaleString()}</TableCell>
                              <TableCell className={payout.hitCap ? 'font-medium text-blue-800' : ''}>
                                ${payout.grossPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{payout.hitCap && '*'}
                              </TableCell>
                              <TableCell>{payout.eAndO > 0 ? `-$${payout.eAndO}` : '-'}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{payout.splits || '-'}</TableCell>
                              <TableCell className="text-right font-medium text-green-600">
                                ${payout.netPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-center">
                                <ReconDocumentPopover
                                  reconDocuments={payout.reconDocuments}
                                  settlementStatementPath={payout.settlementStatementPath}
                                  finalCheckPath={payout.finalCheckPath}
                                  payoutType={payout.type}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Legend for capped payouts */}
                  {payouts.some(p => p.hitCap) && (
                    <div className="p-3 border rounded-lg bg-muted/30 text-sm">
                      <span className="font-medium text-blue-800">*</span>
                      <span className="text-muted-foreground ml-1">
                        Gross amount hit maximum compensation cap.
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Compensation Formula - shown for all users */}
              <div>
                <h3 className="text-sm font-medium mb-4">Compensation Formula</h3>
                <div className="p-4 border rounded-lg bg-muted/50 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Banked Rate:</span>
                    <span className="font-medium">{user.banked_compensation ?? '--'}% of loan amount</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Broker Split:</span>
                    <span className="font-medium">{user.broker_commission_split ?? '--'}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Max Cap per Deal:</span>
                    <span className="font-medium">{user.maximum_compensation ? `$${user.maximum_compensation.toLocaleString()}` : '--'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Override (to recipient):</span>
                    <span className="font-medium">{user.banked_override_bps ?? '--'} bps of loan amount</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Mentor Split:</span>
                    <span className="font-medium">{user.broker_mentor_commission_split ?? '--'}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">E&O Insurance:</span>
                    <span className="font-medium">$50 per loan</span>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 3: History */}
            <TabsContent value="history" className="space-y-6 mt-6">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Compensation History</h3>
                  <p className="text-sm text-muted-foreground">
                    Changes to compensation settings will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Compensation Change Timeline</h3>
                  <div className="relative border-l-2 border-muted pl-6 space-y-6">
                    {history.map((record, index) => (
                      <div key={record.id} className="relative">
                        {/* Timeline dot */}
                        <div className="absolute -left-[31px] w-4 h-4 rounded-full bg-primary border-2 border-background" />

                        <Card>
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm font-medium">
                                {parseAsLocalDate(record.effectiveDate).toLocaleDateString('en-US', {
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </CardTitle>
                              {record.changedBy && (
                                <Badge variant="outline" className="text-xs font-normal">
                                  by {record.changedBy}
                                </Badge>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="grid gap-2 text-sm">
                              {record.bankedCompensation !== null && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Banked Compensation:</span>
                                  <span className="font-medium">{record.bankedCompensation}%</span>
                                </div>
                              )}
                              {record.maximumCompensation !== null && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Max Compensation:</span>
                                  <span className="font-medium">${record.maximumCompensation.toLocaleString()}</span>
                                </div>
                              )}
                              {record.brokerCommissionSplit !== null && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Broker Split:</span>
                                  <span className="font-medium">{record.brokerCommissionSplit}%</span>
                                </div>
                              )}
                              {record.bankedOverrideBps !== null && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Override:</span>
                                  <span className="font-medium">{record.bankedOverrideBps} bps</span>
                                </div>
                              )}
                              {record.overrideRecipientName && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Override Recipient:</span>
                                  <span className="font-medium">{record.overrideRecipientName}</span>
                                </div>
                              )}
                              {record.mentorName && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Mentor:</span>
                                  <span className="font-medium">{record.mentorName}</span>
                                </div>
                              )}
                              {record.brokerMentorCommissionSplit !== null && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Mentor Split:</span>
                                  <span className="font-medium">{record.brokerMentorCommissionSplit}%</span>
                                </div>
                              )}
                              {record.mentorshipActive !== null && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Mentorship:</span>
                                  <Badge variant={record.mentorshipActive ? 'default' : 'secondary'} className="text-xs">
                                    {record.mentorshipActive ? 'Active' : 'Inactive'}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};
